import React from 'react';

import { SxProps, Theme } from '@mui/material/styles';
import { Box as MuiBox, BoxProps as MuiBoxProps } from '../Box';
import { extractPounceProps } from '../../utils/extractPounceProps';
import { mapPouncePropsToSx } from '../../utils/pouncePropsMapper';
import { PounceStyleProps } from '../../utils/pounceTypes';

export interface ImgProps
  extends
    Omit<React.ImgHTMLAttributes<HTMLImageElement>, keyof PounceStyleProps | 'width' | 'height'>,
    PounceStyleProps {
  /** The image URL */
  src: string;

  /** The fallback text of the image */
  alt: string;

  /** The native width of the image. Differs from `width` which is tied to the theme */
  nativeWidth?: number | string;

  /** The native height of the image. Differs from `height` which is tied to the theme */
  nativeHeight?: number | string;

  /** Additional styles to apply (combined with pounce props) */
  sx?: MuiBoxProps['sx'];
}

/**
 * Img - PounceV2 implementation using MUI Box
 *
 * 100% backward compatible with Pounce Img.
 *
 * @example
 * // Using Pounce props
 * <Img src="/logo.svg" alt="Logo" mt={2} p={3} />
 *
 * // Using native size attributes
 * <Img src="/logo.svg" alt="Logo" nativeWidth={322} nativeHeight={70} />
 *
 * // Using MUI sx
 * <Img src="/logo.svg" alt="Logo" sx={{ mt: 2, p: 3 }} />
 */
export const Img = React.forwardRef<HTMLImageElement, ImgProps>(function Img(
  { src, alt, nativeWidth, nativeHeight, sx, ...rest },
  ref,
) {
  // Development-only warnings
  if (process.env.NODE_ENV !== 'production') {
    if (rest.width && !nativeWidth) {
      // eslint-disable-next-line no-console
      console.warn('A width was specified for an image without a `nativeWidth` being defined');
    }

    if (rest.height && !nativeHeight) {
      // eslint-disable-next-line no-console
      console.warn('A height was specified for an image without a `nativeHeight` being defined');
    }
  }

  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);

  // Apply default styles
  const defaultStyles: SxProps<Theme> = {
    maxWidth: '100%',
    height: !nativeHeight ? 'auto' : nativeHeight,
  };
  // Combine default styles, pounce-generated styles, and user-provided sx
  // Order matters: defaults < pounce < sx (user sx wins)
  const combinedSx = { ...defaultStyles, ...pounceStyles, ...sx };

  return (
    <MuiBox
      ref={ref}
      component="img"
      sx={combinedSx}
      {...otherProps}
      {...{
        src,
        alt,
        width: nativeWidth,
        height: nativeHeight,
      }}
    />
  );
});

Img.displayName = 'Img';

export default Img;
